import { useMemo, useCallback, useState } from 'react';
import { graphql, readInlineData, useMutation } from 'react-relay';
import Fuse, { type FuseResultMatch } from 'fuse.js';
import { EpisodeCard, type EpisodeCardData } from './EpisodeCard';
import type { EpisodeList_episode$key, EpisodeList_episode$data } from '@/__generated__/EpisodeList_episode.graphql';
import type { EpisodeListLikeMutation } from '@/__generated__/EpisodeListLikeMutation.graphql';
import type { EpisodeListUnlikeMutation } from '@/__generated__/EpisodeListUnlikeMutation.graphql';

/**
 * Inline fragment for reading episode data synchronously for search indexing.
 * Includes all fields needed for both search and rendering in EpisodeCard.
 */
const EpisodeList_episode = graphql`
  fragment EpisodeList_episode on Episode @inline {
    id
    episodeId
    topicSlug
    title
    summary
    transcript
    sourceLinks
    publishedAt
    durationSeconds
    audioS3Key
    isLikedByViewer
    likeCount
    chapters {
      id
      title
      startTimeMs
      endTimeMs
    }
  }
`;

/**
 * Mutation to like an episode
 */
const likeEpisodeMutation = graphql`
  mutation EpisodeListLikeMutation($input: LikeEpisodeInput!) {
    likeEpisode(input: $input) {
      episode {
        id
        isLikedByViewer
        likeCount
      }
      userErrors {
        message
        field
      }
    }
  }
`;

/**
 * Mutation to unlike an episode
 */
const unlikeEpisodeMutation = graphql`
  mutation EpisodeListUnlikeMutation($input: UnlikeEpisodeInput!) {
    unlikeEpisode(input: $input) {
      episode {
        id
        isLikedByViewer
        likeCount
      }
      userErrors {
        message
        field
      }
    }
  }
`;

interface EpisodeListProps {
  episodes: ReadonlyArray<EpisodeList_episode$key>;
  searchQuery: string;
  onSelectEpisode: (episodeData: EpisodeList_episode$data) => void;
  currentEpisodeId?: string;
  isPlaying?: boolean;
  topicSlug: string;
  topicName: string;
  showLikeButtons?: boolean;
}

export type SearchResult = {
  episodeData: EpisodeList_episode$data;
  matches?: readonly FuseResultMatch[];
};

export function EpisodeList({
  episodes,
  searchQuery,
  onSelectEpisode,
  currentEpisodeId,
  isPlaying,
  topicSlug,
  topicName,
  showLikeButtons,
}: EpisodeListProps) {
  const [pendingLikeEpisodeId, setPendingLikeEpisodeId] = useState<string | null>(null);
  const [commitLike] = useMutation<EpisodeListLikeMutation>(likeEpisodeMutation);
  const [commitUnlike] = useMutation<EpisodeListUnlikeMutation>(unlikeEpisodeMutation);

  // Read all episode data for search indexing using inline data
  const episodeDataList = useMemo(() => {
    return episodes.map((ref) => readInlineData(EpisodeList_episode, ref));
  }, [episodes]);

  const handleLike = useCallback((episodeData: EpisodeList_episode$data) => {
    setPendingLikeEpisodeId(episodeData.episodeId);
    commitLike({
      variables: {
        input: {
          topicSlug: episodeData.topicSlug,
          episodeId: episodeData.episodeId,
        },
      },
      optimisticUpdater: (store) => {
        const episodeRecord = store.get(episodeData.id);
        if (episodeRecord) {
          const currentLikeCount = episodeRecord.getValue('likeCount') as number | null;
          episodeRecord.setValue(true, 'isLikedByViewer');
          episodeRecord.setValue((currentLikeCount ?? 0) + 1, 'likeCount');
        }
      },
      onCompleted: () => {
        setPendingLikeEpisodeId(null);
      },
      onError: (error) => {
        console.error('Failed to like episode:', error);
        setPendingLikeEpisodeId(null);
      },
    });
  }, [commitLike]);

  const handleUnlike = useCallback((episodeData: EpisodeList_episode$data) => {
    setPendingLikeEpisodeId(episodeData.episodeId);
    commitUnlike({
      variables: {
        input: {
          topicSlug: episodeData.topicSlug,
          episodeId: episodeData.episodeId,
        },
      },
      optimisticUpdater: (store) => {
        const episodeRecord = store.get(episodeData.id);
        if (episodeRecord) {
          const currentLikeCount = episodeRecord.getValue('likeCount') as number | null;
          episodeRecord.setValue(false, 'isLikedByViewer');
          episodeRecord.setValue(Math.max(0, (currentLikeCount ?? 0) - 1), 'likeCount');
        }
      },
      onCompleted: () => {
        setPendingLikeEpisodeId(null);
      },
      onError: (error) => {
        console.error('Failed to unlike episode:', error);
        setPendingLikeEpisodeId(null);
      },
    });
  }, [commitUnlike]);

  // Enhanced Fuse.js configuration with best practices
  const fuse = useMemo(
    () =>
      new Fuse(episodeDataList, {
        // Search across multiple fields with weighted importance
        keys: [
          { name: 'title', weight: 2.0 }, // Title matches are most important
          { name: 'summary', weight: 1.5 }, // Summary is second most important
          { name: 'transcript', weight: 1.0 }, // Transcript content for deep search
          { name: 'sourceLinks', weight: 0.5 }, // Source links least important
        ],
        // Optimized threshold for fuzzy matching (0.3 = stricter, 0.6 = more lenient)
        threshold: 0.3,
        // Ignore location for better results across long text
        ignoreLocation: true,
        // Minimum character length before searching
        minMatchCharLength: 2,
        // Include match metadata for highlighting
        includeMatches: true,
        // Include score for debugging/sorting
        includeScore: true,
        // Use extended search syntax (e.g., 'word | "exact phrase"')
        useExtendedSearch: false,
      }),
    [episodeDataList]
  );

  const searchResults = useMemo((): SearchResult[] => {
    if (!searchQuery.trim()) {
      return episodeDataList.map((data) => ({
        episodeData: data,
      }));
    }

    const results = fuse.search(searchQuery);

    return results.map((result) => ({
      episodeData: result.item,
      matches: result.matches,
    }));
  }, [fuse, searchQuery, episodeDataList]);

  const resultCount = searchResults.length;
  const isSearching = searchQuery.trim().length > 0;

  if (resultCount === 0) {
    return (
      <div className="mt-8 text-center text-gray-600 dark:text-gray-400">
        {isSearching ? 'No episodes match your search.' : 'No episodes yet.'}
      </div>
    );
  }

  return (
    <>
      {isSearching && (
        <div className="mt-4 text-sm text-gray-600 dark:text-gray-400">
          Found {resultCount} {resultCount === 1 ? 'episode' : 'episodes'}
        </div>
      )}
      <div className="mt-6 space-y-4">
        {searchResults.map(({ episodeData, matches }) => (
          <EpisodeCard
            key={episodeData.episodeId}
            episode={episodeData as EpisodeCardData}
            isCurrentEpisode={episodeData.episodeId === currentEpisodeId}
            isPlaying={episodeData.episodeId === currentEpisodeId && !!isPlaying}
            onSelect={() => onSelectEpisode(episodeData)}
            topicSlug={topicSlug}
            topicName={topicName}
            searchMatches={matches}
            searchQuery={isSearching ? searchQuery : undefined}
            showLikeButton={showLikeButtons}
            onLike={() => handleLike(episodeData)}
            onUnlike={() => handleUnlike(episodeData)}
            isLikeMutationPending={pendingLikeEpisodeId === episodeData.episodeId}
          />
        ))}
      </div>
    </>
  );
}

// Re-export types for consumers
export type { EpisodeList_episode$data, EpisodeList_episode$key };
